import axios, { RawAxiosRequestHeaders } from 'axios';
import { Storage } from 'repositories/Storage';
import {
  MONTHLY_TRANSFER_LIMIT_EXCEEDED_MESSAGE,
  RepositoryError,
} from 'repositories/Error';
import { ReleaseVersionDomain } from 'models/ReleaseVersion';
import { LanguageDomain } from 'models/Language';
import { APIDomain } from 'models/API';

export const client = axios.create({
  baseURL: `${APIDomain.origin()}/api`,
});

interface HeadersArgs {
  authorizationToken?: string;
  iphoneUid?: string;
}

export const authHeaders = async (
  args?: HeadersArgs
): Promise<RawAxiosRequestHeaders> => {
  const authorizationToken =
    args?.authorizationToken || (await Storage.get('token'));
  const iphoneUid = args?.iphoneUid || (await Storage.get('iphone_uid'));
  const headers: RawAxiosRequestHeaders = {};

  if (authorizationToken) {
    headers.Authorization = `token ${authorizationToken}`;
  }

  headers['x-deploygate-unauthorized-user'] =
    headers.Authorization === undefined ? 'true' : 'false';

  if (iphoneUid) {
    headers['x-deploygate-ios-device-token'] = iphoneUid;
  }

  return headers;
};

export const apiVersion2Headers = (): RawAxiosRequestHeaders => {
  const headers: RawAxiosRequestHeaders = {};
  headers['x-deploygate-api-version'] = '2';

  return headers;
};

client.interceptors.request.use((config) => {
  config.headers.set(
    'x-deploygate-client-id',
    'ios-spa/' + ReleaseVersionDomain.currentVersion()
  );
  config.headers.set('Accept-Language', LanguageDomain.getDefaultLanguage());
  return config;
});

client.interceptors.response.use(
  (response) => {
    const { status, data } = response;
    if (data.error) {
      // 2xx系ステータスでAPIサーバーからエラーが返された場合
      throw new RepositoryError(status, data?.message);
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      // 429エラーかつ月間データ転送量上限超過の場合は専用のメッセージを返す
      const messageNormalized = data?.message?.trim?.()?.toLowerCase?.();
      if (
        status === 429 &&
        messageNormalized === MONTHLY_TRANSFER_LIMIT_EXCEEDED_MESSAGE
      ) {
        throw new RepositoryError(
          status,
          MONTHLY_TRANSFER_LIMIT_EXCEEDED_MESSAGE,
          true
        );
      }
      if (data.error) {
        // 2xx系ステータス以外のstatusでAPIサーバーからエラーが返された場合
        throw new RepositoryError(status, data?.message, true, data?.details);
      } else {
        // APIサーバーからエラーstatusで返されたのに、response内のerrorがtrueではない場合
        const statusCode = Number(status);
        if (!isNaN(statusCode) && statusCode >= 400 && statusCode < 500) {
          // クライアント側のエラーの場合
          throw new RepositoryError(status, error.message);
        }
      }
    }

    // クライアント側のネットワーク疎通に関する問題なので、Status Codeは存在しないものにする
    throw new RepositoryError(999, 'not connected to internet', true);
  }
);
